'use client';

import { ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/app/utils';

const useIsoLayoutEffect = typeof window !== 'undefined' ? React.useLayoutEffect : React.useEffect;

/** 拖动超过这个像素才算拖拽，否则视为点击 */
const TAP_SLOP = 6;

export interface CoverflowSlide {
  src: string;
  alt: string;
  title?: string;
  subtitle?: string;
  meta?: { label: string; value: string }[];
}

export interface CoverflowCarouselProps {
  slides: CoverflowSlide[];
  /** Degrees the first neighbour tilts. */
  rotate?: number;
  /** How far the first neighbour recedes, as a fraction of card width. */
  depth?: number;
  /** Viewer distance as a multiple of card width — smaller is a wider lens. */
  perspective?: number;
  /** Exponent on distance. Below 1 the rake eases off as cards travel out. */
  falloff?: number;
  /** Opacity lost per step from the centre. */
  fade?: number;
  /** Any CSS length. Everything else is derived from it, so the rake scales. */
  cardWidth?: string;
  /** Card height as a multiple of card width. 1 is square. */
  cardAspect?: number;
  /** Space between cards, as a fraction of card width. */
  gap?: number;
  /** Extra scale per step from the centre. 1 keeps size; lower shrinks far cards faster. */
  shrink?: number;
  /** Blur in px added per step from the centre. */
  blur?: number;
  /** Black overlay opacity added per step from the centre. */
  shade?: number;
  loop?: boolean;
  showCaption?: boolean;
  showPagination?: boolean;
  showNavigation?: boolean;
  /** Names the carousel for assistive tech. */
  label?: string;
  /** Labels for the arrow buttons, for i18n. */
  previousLabel?: string;
  nextLabel?: string;
  /** Fires whenever the centred card changes, by drag, key, click or arrow. */
  onSelectedChange?: (index: number) => void;
  /** Rendered under the caption for the centred slide, e.g. a confirm button. */
  renderCaptionAction?: (index: number) => React.ReactNode;
  /** Draw each slide's title on the card, over a bottom black gradient. */
  showCardTitle?: boolean;
  /** Rendered inside the centred card, under its title, e.g. a confirm button. */
  renderCardAction?: (index: number) => React.ReactNode;
  className?: string;
  cardClassName?: string;
}

export function CoverflowCarousel({
  slides,
  rotate = 44,
  depth = 0.6,
  perspective = 3,
  falloff = 0.56,
  fade = 0.1,
  cardWidth = 'clamp(148px, 22vw, 260px)',
  cardAspect = 1,
  gap = 0.05,
  shrink = 1,
  blur = 0,
  shade = 0,
  loop = true,
  showCaption = false,
  showPagination = false,
  showNavigation = false,
  label = 'Cover carousel',
  previousLabel = 'Previous slide',
  nextLabel = 'Next slide',
  onSelectedChange,
  renderCaptionAction,
  showCardTitle = false,
  renderCardAction,
  className,
  cardClassName,
}: CoverflowCarouselProps) {
  const count = slides.length;

  const frameRef = React.useRef<HTMLDivElement>(null);
  const cardRefs = React.useRef<(HTMLFieldSetElement | null)[]>([]);
  /** Fractional card index at the centre. The single source of truth. */
  const posRef = React.useRef(0);
  /** Where the current settle is headed. Stepping off `pos` instead would
      swallow a keypress that lands mid-flight, before the round-off moves. */
  const targetRef = React.useRef(0);
  const widthRef = React.useRef(0);
  const rafRef = React.useRef<number | null>(null);
  const dragRef = React.useRef<{
    id: number;
    x: number;
    pos: number;
    v: number;
    t: number;
    moved: boolean;
  } | null>(null);

  const [selected, setSelected] = React.useState(0);
  const onSelectedChangeRef = React.useRef(onSelectedChange);
  onSelectedChangeRef.current = onSelectedChange;
  React.useEffect(() => {
    onSelectedChangeRef.current?.(selected);
  }, [selected]);

  /** Nearest whole card, folded back into 0..count-1. */
  const indexAt = React.useCallback((pos: number) => ((Math.round(pos) % count) + count) % count, [count]);

  // Paint straight to the DOM. Sixty state updates a second would re-render
  // every card for numbers React never needs to see.
  const paint = React.useCallback(() => {
    const width = widthRef.current;
    if (!width) return;
    const pitch = width * (1 + gap);
    const pos = posRef.current;

    cardRefs.current.forEach((card, index) => {
      if (!card) return;

      // Fold the distance into the shorter way round the ring. This is the
      // whole looping mechanism — no cloned nodes, no shuffling the DOM.
      let offset = index - pos;
      if (loop) {
        offset = ((offset % count) + count) % count;
        if (offset > count / 2) offset -= count;
      }

      const distance = Math.abs(offset);
      // Both the tilt and the recession ease off as cards travel out.
      const ramp = distance ** falloff;
      // Capped short of edge-on so a far card never turns its back.
      const tilt = Math.min(rotate * ramp, 82) * Math.sign(offset);

      // Far cards shrink faster than perspective alone would; the centre stays at full size.
      const scale = Math.max(0.2, shrink ** distance);
      card.style.transform = `translateX(calc(-50% + ${offset * pitch}px)) translateZ(${-depth * width * ramp}px) rotateY(${-tilt}deg) scale(${scale})`;

      // A card is teleported across the ring at exactly half a turn out, so it
      // has to be gone by then or the jump is visible.
      const edge = loop ? Math.min(1, Math.max(0, count / 2 - distance)) : 1;
      card.style.opacity = String(Math.max(0, 1 - fade * distance) * edge);
      card.style.filter = blur > 0 && distance > 0.01 ? `blur(${(blur * Math.min(distance, 4)).toFixed(2)}px)` : '';
      // Darkening overlay deepens with distance, so the centre card reads as lit.
      card.style.setProperty('--cf-shade', String(Math.min(0.85, shade * distance)));
      card.style.zIndex = String(100 - Math.round(distance));
    });
  }, [blur, count, depth, fade, falloff, gap, loop, rotate, shade, shrink]);

  const settle = React.useCallback(
    (target: number) => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      targetRef.current = target;
      setSelected(indexAt(target));

      const step = () => {
        const remaining = target - posRef.current;
        if (Math.abs(remaining) < 0.0004) {
          posRef.current = target;
          paint();
          rafRef.current = null;
          return;
        }
        // Exponential ease-out, not a spring.
        posRef.current += remaining * 0.16;
        paint();
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    },
    [indexAt, paint],
  );

  const clamp = React.useCallback((pos: number) => (loop ? pos : Math.max(0, Math.min(count - 1, pos))), [count, loop]);

  const goTo = React.useCallback(
    (index: number) => {
      // Take the shorter way round rather than unwinding the whole ring.
      const target = loop ? index + Math.round((targetRef.current - index) / count) * count : index;
      settle(clamp(target));
    },
    [clamp, count, loop, settle],
  );

  const nudge = React.useCallback((by: number) => settle(clamp(Math.round(targetRef.current) + by)), [clamp, settle]);

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // Buttons inside a card handle their own clicks; pointer capture would swallow them.
    if ((event.target as HTMLElement).closest('button')) return;
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    targetRef.current = posRef.current;
    dragRef.current = {
      id: event.pointerId,
      x: event.clientX,
      pos: posRef.current,
      v: 0,
      t: performance.now(),
      moved: false,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;

    const pitch = widthRef.current * (1 + gap);
    if (!pitch) return;
    if (Math.abs(event.clientX - drag.x) > TAP_SLOP) drag.moved = true;

    const now = performance.now();
    const previous = posRef.current;
    posRef.current = clamp(drag.pos - (event.clientX - drag.x) / pitch);
    // Cards per second, for the throw.
    drag.v = ((posRef.current - previous) / Math.max(now - drag.t, 1)) * 1000;
    drag.t = now;

    const index = indexAt(posRef.current);
    if (index !== selected) setSelected(index);
    paint();
  };

  const endDrag = (event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.id !== event.pointerId) return;
    dragRef.current = null;
    if (!drag.moved) {
      // A tap, not a drag: bring the tapped card to the centre. Under
      // preserve-3d the hit test lands on the flat stage, not the tilted card,
      // so work out which card sits under the pointer from the same pitch the
      // paint uses: the card whose projected centre is closest wins.
      const frame = frameRef.current;
      const pitch = widthRef.current * (1 + gap);
      if (frame && pitch) {
        const rect = frame.getBoundingClientRect();
        const dx = event.clientX - (rect.left + rect.width / 2);
        let best = -1;
        let bestDistance = Number.POSITIVE_INFINITY;
        cardRefs.current.forEach((card, index) => {
          if (!card) return;
          const box = card.getBoundingClientRect();
          if (event.clientY < box.top || event.clientY > box.bottom) return;
          const distance = Math.abs(event.clientX - (box.left + box.width / 2));
          if (distance < bestDistance && Number(card.style.opacity || 1) > 0.05) {
            best = index;
            bestDistance = distance;
          }
        });
        if (best >= 0 && Math.abs(dx) > widthRef.current * 0.5) {
          goTo(best);
          return;
        }
      }
    }
    // Let a flick carry, but never more than two cards.
    const carried = Math.max(-2, Math.min(2, drag.v * 0.18));
    settle(clamp(Math.round(posRef.current + carried)));
  };

  // Card width drives pitch, depth and perspective, so it is the only thing
  // worth measuring — and only when the box actually changes.
  useIsoLayoutEffect(() => {
    const frame = frameRef.current;
    if (!frame) return;

    const measure = () => {
      const card = cardRefs.current[0];
      if (!card) return;
      widthRef.current = card.offsetWidth;
      paint();
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(frame);
    return () => observer.disconnect();
  }, [paint]);

  React.useEffect(
    () => () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    },
    [],
  );

  const active = slides[selected];

  return (
    <section
      className={cn('w-full', className)}
      style={{ ['--cf-card' as string]: cardWidth }}
      aria-roledescription="carousel"
      aria-label={label}
    >
      <div className="relative">
        <div
          ref={frameRef}
          role="slider"
          tabIndex={0}
          aria-label={label}
          aria-valuemin={1}
          aria-valuemax={count}
          aria-valuenow={selected + 1}
          aria-valuetext={active?.title ?? active?.alt}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onKeyDown={(event) => {
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              nudge(-1);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              nudge(1);
            }
          }}
          // Vertical padding keeps the drop shadows clear of the overflow clip.
          className="cursor-grab overflow-hidden py-9 outline-none ring-ring focus-visible:ring-2 active:cursor-grabbing"
          style={{
            perspective: `calc(var(--cf-card) * ${perspective})`,
            // Horizontal drag is ours; the page keeps vertical scrolling.
            touchAction: 'pan-y',
          }}
        >
          <div
            className="relative select-none"
            style={{ height: `calc(var(--cf-card) * ${cardAspect})`, transformStyle: 'preserve-3d' }}
          >
            {slides.map((slide, index) => (
              <fieldset
                key={slide.src}
                ref={(node) => {
                  cardRefs.current[index] = node;
                }}
                aria-roledescription="slide"
                aria-current={index === selected ? 'true' : undefined}
                aria-label={`${index + 1} / ${count}`}
                data-coverflow-index={index}
                className={cn(
                  'absolute top-0 left-1/2 m-0 min-w-0 overflow-hidden rounded-2xl border-0 bg-muted p-0 shadow-xl will-change-transform',
                  cardClassName,
                )}
                style={{ width: 'var(--cf-card)', height: `calc(var(--cf-card) * ${cardAspect})` }}
              >
                <img
                  src={slide.src}
                  alt={slide.alt}
                  draggable={false}
                  className="h-full w-full select-none object-cover object-top"
                />
                {showCardTitle && slide.title ? (
                  <div className="absolute inset-x-0 bottom-0 z-10 flex flex-col items-center gap-3 bg-gradient-to-t from-black/85 via-black/45 to-transparent px-4 pt-16 pb-4 text-center">
                    <span className="pointer-events-none max-w-full truncate font-medium text-sm text-white">
                      {slide.title}
                    </span>
                    {renderCardAction && index === selected ? renderCardAction(index) : null}
                  </div>
                ) : null}
                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 bg-black"
                  style={{ opacity: 'var(--cf-shade, 0)' }}
                />
              </fieldset>
            ))}
          </div>
        </div>

        {showNavigation && (
          <>
            <button
              type="button"
              aria-label={previousLabel}
              onClick={() => nudge(-1)}
              className="-translate-y-1/2 absolute top-1/2 left-3 z-[200] rounded-full bg-background/70 p-2 text-foreground backdrop-blur transition hover:bg-background"
            >
              <ChevronLeft className="size-5" />
            </button>
            <button
              type="button"
              aria-label={nextLabel}
              onClick={() => nudge(1)}
              className="-translate-y-1/2 absolute top-1/2 right-3 z-[200] rounded-full bg-background/70 p-2 text-foreground backdrop-blur transition hover:bg-background"
            >
              <ChevronRight className="size-5" />
            </button>
          </>
        )}
      </div>

      {showCaption && (active?.title || renderCaptionAction) && (
        <div
          key={selected}
          className={cn('fade-in flex animate-in flex-col items-center px-6 duration-300', !showCardTitle && 'mt-2')}
        >
          {showCardTitle ? null : (
            <p className="font-semibold text-[15px] text-foreground tracking-tight">{active?.title}</p>
          )}
          {renderCaptionAction ? (
            <div className={showCardTitle ? undefined : 'mt-3'}>{renderCaptionAction(selected)}</div>
          ) : null}
          {active?.subtitle && <p className="mt-1 text-[13px] text-muted-foreground">{active.subtitle}</p>}
          {active?.meta && active.meta.length > 0 && (
            <dl className="mt-10 w-full max-w-[230px] text-[12px]">
              {active.meta.map((row) => (
                <div key={row.label} className="flex justify-between py-[5px]">
                  <dt className="text-muted-foreground">{row.label}</dt>
                  <dd className="font-medium text-foreground">{row.value}</dd>
                </div>
              ))}
            </dl>
          )}
        </div>
      )}

      {showPagination && (
        <div className="mt-6 flex items-center justify-center gap-2">
          {slides.map((slide, index) => (
            <button
              key={slide.src}
              type="button"
              aria-label={slide.title ?? slide.alt}
              aria-current={index === selected}
              onClick={() => goTo(index)}
              className={cn(
                'size-2 rounded-full bg-foreground transition-opacity',
                index === selected ? 'opacity-100' : 'opacity-30',
              )}
            />
          ))}
        </div>
      )}
    </section>
  );
}
